import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isTopic } from '@tunein/web-common';
import * as PlayerActions from 'src/common/actions/player';
import { syncedPlayerActions } from 'src/common/decorators/connectWithPlayer';
import { playerStatuses } from 'src/common/constants/playerStatuses';
import cssVariables from 'src/common/styles/variables';
import CircularProgress from '../../../shared/CircularProgress';
import PopOutButton from '../../../shared/button/PopOutButton';
import { selectAutoPlayGuideItem } from '../../../../selectors/player';
import { isMedium } from '../../../../utils/breakpoints';
import { playerStatusToSvgIconMap } from '../../../../utils/playerStatusToIconMap';
import css from './player-play-button.module.scss';

export class PlayButton extends Component {
  static propTypes = {
    playerActions: PropTypes.object.isRequired,
    playerStatus: PropTypes.string.isRequired,
    breakpoint: PropTypes.number.isRequired,
    canScrub: PropTypes.bool,
    guideItem: PropTypes.object,
    externalHtmlPlayerUrl: PropTypes.string,
    className: PropTypes.string,
    customHeight: PropTypes.number,
    customWidth: PropTypes.number,
    customFill: PropTypes.string,
    progressColor: PropTypes.string,
    onClick: PropTypes.func,
  };

  static defaultProps = {
    className: '',
  };

  handleClick = () => {
    const {
      playerStatus,
      canScrub,
      playerActions,
      guideItem,
      onClick,
      disablePreroll,
    } = this.props;

    if (onClick) {
      onClick();
    }

    const playerAction = syncedPlayerActions[playerStatus];

    if (playerAction) {
      playerAction(playerActions, canScrub, guideItem, disablePreroll);
    }
  };

  render() {
    const {
      playerStatus,
      canScrub,
      externalHtmlPlayerUrl,
      className,
      breakpoint,
      customHeight,
      customWidth,
      customFill,
      progressColor,
    } = this.props;

    const isMediumBreakpoint = isMedium(breakpoint);
    const predeterminedHeight = isMediumBreakpoint ? 45 : 40;
    const predeterminedWidth = isMediumBreakpoint ? 45 : 40;

    const height = customHeight || predeterminedHeight;
    const width = customWidth || predeterminedWidth;

    if (playerStatus === playerStatuses.connecting) {
      return (
        <CircularProgress
          id="playerLoadingSpinner"
          className={className}
          data-testid={`player-status-${playerStatus}`}
          size={width}
          color={progressColor || cssVariables['--secondary-color-1']}
          thickness={2}
        />
      );
    }

    if (playerStatus === playerStatuses.popout) {
      return (
        <div className={className} data-testid="popoutButtonWrapper">
          <PopOutButton
            externalHtmlPlayerUrl={externalHtmlPlayerUrl}
            dataTestId={`player-status-${playerStatus}`}
            width={`${width}`}
            height={`${height}`}
          />
        </div>
      );
    }

    const PlayButtonStateRepresentation =
      playerStatusToSvgIconMap[playerStatus](canScrub);

    return (
      <div className={className} data-testid="playButtonWrapper">
        <PlayButtonStateRepresentation
          id="playerActionButton"
          dataTestId={`player-status-${playerStatus}`}
          className={css.playerPlayButton}
          onClick={this.handleClick}
          fillButtonBackground={customFill}
          height={`${height}`}
          width={`${width}`}
        />
      </div>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const { player, profiles } = state;
  const {
    playerStatus: playerStatusFromStore,
    canScrub,
    externalHtmlPlayerUrl,
    nowPlaying = {},
    tunedGuideId: playerGuideId,
  } = player;
  const autoPlayGuideItem = selectAutoPlayGuideItem(state);
  const guideItem =
    (ownProps.guideItem?.guideId && ownProps.guideItem) ||
    profiles[nowPlaying.primaryGuideId] ||
    (autoPlayGuideItem?.guideId && autoPlayGuideItem) ||
    {};
  const guideIdToPlay = isTopic(nowPlaying.secondaryGuideId)
    ? nowPlaying.secondaryGuideId
    : nowPlaying.primaryGuideId || guideItem.guideId;
  const playerStatus =
    guideIdToPlay === playerGuideId ||
    playerStatusFromStore === playerStatuses.connecting
      ? playerStatusFromStore
      : playerStatuses.idle;

  return {
    playerStatus,
    canScrub,
    externalHtmlPlayerUrl,
    guideItem,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    playerActions: bindActionCreators(PlayerActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PlayButton);
